import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import {
  PhFile,
  PhFileCsv,
  PhFileDoc,
  PhFileImage,
  PhFileJpg,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFileText,
  PhFileTxt,
  PhFileXls,
  PhFileZip,
  PhCheckCircle,
  PhWarning,
  PhTrash,
  PhCaretDown,
  PhCaretUp,
} from 'ember-phosphor-icons';
import { Chip } from 'tio-ui/components/buttons';
import { Section } from 'tio-ui/components/layout';
import { Modal, Footer as ModalFooter } from 'tio-ui/components/modal';
import { or } from 'tio-ui/utilities';
import safeFormatDate from '../../../../../helpers/safe-format-date.ts';
import type TasAssetModel from '../../../../../models/tas-asset.ts';
import type AiRecommendationModel from '../../../../../models/ai-recommendation.ts';
import type AssetSummaryModel from '../../../../../models/asset-summary.ts';

/**
 * Attachments Analysis View
 * Displays AI-generated document summaries for uploaded attachments
 * Now uses asset-summary as primary data source
 */
export interface AttachmentsAnalysisViewSignature {
  Args: {
    recommendation?: AiRecommendationModel;
    attachments: TasAssetModel[];
    assetSummaries?: AssetSummaryModel[];
    deleteAttachment?: {
      perform: (asset: TasAssetModel) => Promise<void>;
      isRunning: boolean;
    };
    isDeleted?: (asset: TasAssetModel) => boolean;
  };
}

interface TranscriptInfo {
  courses?: Array<{
    grade?: string;
    course?: string;
    period?: string;
    credits?: number;
    course_detail?: string;
  }>;
  program?: string;
  summary?: string;
  institution?: string;
  student_name?: string;
}

interface ReceiptInfo {
  entity?: string;
  summary?: string;
  line_items?: Array<{
    line_date?: string;
    line_item?: string;
    line_type?: string;
    line_amount?: number;
    line_details?: string;
  }>;
  payer_name?: string;
}

interface DocumentResult {
  general?: {
    summary: string;
  };
  transcript?: TranscriptInfo;
  receipt?: ReceiptInfo;
  confidence_score?: number;
}

interface DocumentSummary {
  result?: DocumentResult;
  is_error?: boolean;
  file_name?: string;
}

class AttachmentsAnalysisView extends Component<AttachmentsAnalysisViewSignature> {
  @tracked attachmentToDelete: TasAssetModel | null = null;
  @tracked showConfirmModal = false;
  @tracked expandedAttachments = new Set<string>();

  toggleExpanded = (attachmentId: string | null) => {
    if (!attachmentId) return;
    if (this.expandedAttachments.has(attachmentId)) {
      this.expandedAttachments.delete(attachmentId);
    } else {
      this.expandedAttachments.add(attachmentId);
    }
    // Force reactivity by reassigning
    this.expandedAttachments = new Set(this.expandedAttachments);
  };

  isExpanded = (attachmentId: string | null): boolean => {
    if (!attachmentId) return false;
    return this.expandedAttachments.has(attachmentId);
  };

  confirmDelete = (attachment: TasAssetModel) => {
    this.attachmentToDelete = attachment;
    this.showConfirmModal = true;
  };

  cancelDelete = () => {
    this.attachmentToDelete = null;
    this.showConfirmModal = false;
  };

  performDelete = async () => {
    if (this.attachmentToDelete && this.args.deleteAttachment) {
      await this.args.deleteAttachment.perform(this.attachmentToDelete);
      this.attachmentToDelete = null;
      this.showConfirmModal = false;
    }
  };

  /**
   * Get asset summary for a specific attachment
   * This is now the primary source of document analysis data
   */
  getAssetSummaryForAttachment = (attachment: TasAssetModel): AssetSummaryModel | null => {
    try {
      if (!attachment) {
        return null;
      }

      // First try to get from the attachment's direct relationship
      if (attachment.assetSummary) {
        return attachment.assetSummary;
      }

      // Fallback to searching in the passed array
      if (this.args.assetSummaries) {
        const summary = this.args.assetSummaries.find(
          (summary) => summary.digitalAsset?.id === attachment.id
        );
        return summary || null;
      }

      return null;
    } catch (error) {
      console.error('Error getting asset summary for attachment:', error);
      return null;
    }
  };

  /**
   * Extract document summary from asset summary
   * Asset summary.summary contains the full analysis including result, is_error, file_name
   */
  getDocumentSummaryFromAssetSummary = (
    assetSummary: AssetSummaryModel
  ): DocumentSummary | null => {
    try {
      if (!assetSummary) {
        return null;
      }

      if (!assetSummary.summary) {
        return null;
      }

      // The summary attribute contains the full DocumentSummary structure
      // Cast it to DocumentSummary type
      const summary = assetSummary.summary as unknown as DocumentSummary;

      return summary;
    } catch (error) {
      console.error('Error extracting document summary from asset summary:', error);
      return null;
    }
  };

  openAttachment = (url: string) => {
    window.open(url, '_blank');
  };

  /**
   * Get the appropriate file icon component based on file extension
   */
  getFileIcon = (attachment: TasAssetModel) => {
    const extension = attachment.extension?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return PhFilePdf;
      case 'doc':
      case 'docx':
        return PhFileDoc;
      case 'xls':
      case 'xlsx':
        return PhFileXls;
      case 'ppt':
      case 'pptx':
        return PhFilePpt;
      case 'txt':
        return PhFileTxt;
      case 'zip':
        return PhFileZip;
      case 'jpg':
      case 'jpeg':
        return PhFileJpg;
      case 'png':
        return PhFilePng;
      case 'gif':
      case 'bmp':
      case 'svg':
        return PhFileImage;
      case 'csv':
        return PhFileCsv;
      case 'text':
        return PhFileText;
      default:
        return PhFile;
    }
  };

  /**
   * Get human-readable document type label and intent for chip
   * Infers document type from AI analysis structure and summary text
   */
  getDocumentTypeInfo = (
    docSummary?: DocumentSummary
  ): { label: string; intent: 'success' | 'warning' | 'default' | 'primary' | 'danger' } => {
    if (!docSummary?.result) {
      return { label: 'Unknown', intent: 'default' };
    }

    // Check structured data first (most reliable)
    if (docSummary.result.receipt) {
      return { label: 'Receipt', intent: 'success' };
    }
    if (docSummary.result.transcript) {
      return { label: 'Transcript', intent: 'primary' };
    }

    // Check general summary text for keywords
    const summary = docSummary.result.general?.summary?.toLowerCase() || '';

    if (summary.includes('receipt') || summary.includes('invoice') || summary.includes('payment')) {
      return { label: 'Receipt', intent: 'success' };
    }
    if (summary.includes('transcript')) {
      return { label: 'Transcript', intent: 'primary' };
    }
    if (
      summary.includes('enrollment') ||
      summary.includes('enrolment') ||
      summary.includes('proof of enrollment') ||
      summary.includes('enrollment verification')
    ) {
      return { label: 'Enrollment Verification', intent: 'primary' };
    }
    if (summary.includes('grade report') || summary.includes('report card')) {
      return { label: 'Grade Report', intent: 'primary' };
    }
    if (summary.includes('syllabus')) {
      return { label: 'Syllabus', intent: 'default' };
    }
    if (
      summary.includes('completion') ||
      summary.includes('certificate') ||
      summary.includes('certification')
    ) {
      return { label: 'Completion Certificate', intent: 'success' };
    }
    if (summary.includes('bill') || summary.includes('tuition statement')) {
      return { label: 'Tuition Bill', intent: 'success' };
    }

    // Fallback to generic document
    return { label: 'Document', intent: 'default' };
  };

  <template>
    <div class="p-2 overflow-x-hidden">
      <h3 class="text-base font-semibold mb-4 text-gray-800 dark:text-gray-200">Document Analysis</h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
        AI-generated summaries and analysis of uploaded documents
      </p>

      <div class="space-y-4">
        {{#if @attachments.length}}
          {{#each @attachments as |attachment|}}
            {{! Get asset summary for this attachment }}
            {{#let (this.getAssetSummaryForAttachment attachment) as |assetSummary|}}
              {{#if assetSummary}}
                {{! Extract document summary from asset summary }}
                {{#let (this.getDocumentSummaryFromAssetSummary assetSummary) as |docSummary|}}
                  {{#if docSummary}}
                    <Section>
                      <:header>
                        <div class="flex flex-col gap-2">
                          <div class="flex items-center gap-2 w-full">
                            {{#if (@isDeleted attachment)}}
                              <div class="flex flex-col flex-1 min-w-0 gap-1">
                                <div
                                  class="flex items-center gap-2 text-gray-500 dark:text-gray-400 italic min-w-0"
                                >
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    <FileIcon @size="24" class="flex-shrink-0" />
                                  {{/let}}
                                  <span class="block">{{docSummary.file_name}}</span>
                                  <span class="flex-shrink-0 text-sm">(Deleted)</span>
                                </div>
                                {{#if attachment.attachmentDeletedAt}}
                                  <div class="text-xs text-gray-400 dark:text-gray-600 ml-7">
                                    Deleted
                                    {{safeFormatDate
                                      attachment.attachmentDeletedAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                            {{else}}
                              <div class="flex flex-col flex-1 min-w-0 gap-1">
                                <div class="flex items-center gap-2 min-w-0 overflow-hidden">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="flex items-center gap-2 min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                        title="View {{docSummary.file_name}}"
                                      >
                                        <FileIcon
                                          @size="24"
                                          class="text-purple-600 flex-shrink-0"
                                        />
                                        <span
                                          class="font-semibold block truncate"
                                        >{{docSummary.file_name}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="text-purple-600 flex-shrink-0" />
                                      <span
                                        class="font-semibold block truncate max-w-96"
                                      >{{docSummary.file_name}}</span>
                                    {{/if}}
                                  {{/let}}
                                </div>
                                {{#if attachment.createdAt}}
                                  <div class="text-xs text-gray-500 dark:text-gray-400 ml-7">
                                    Uploaded
                                    {{safeFormatDate
                                      attachment.createdAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <div class="flex items-center gap-2 flex-shrink-0 ml-auto">
                                {{#if docSummary.result.confidence_score}}
                                  <Chip @intent="primary" @size="sm">Score:
                                    {{docSummary.result.confidence_score}}/5</Chip>
                                {{/if}}
                                {{#if @deleteAttachment}}
                                  <button
                                    type="button"
                                    class="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Delete attachment"
                                    aria-label="Delete attachment"
                                    {{on "click" (fn this.confirmDelete attachment)}}
                                  >
                                    <PhTrash @size="18" />
                                  </button>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                      </:header>
                      <:body>
                        <div class="break-words">
                          {{! General Summary }}
                          {{#if docSummary.result.general.summary}}
                            <div class="mb-4">
                              {{#let (this.getDocumentTypeInfo docSummary) as |typeInfo|}}
                                <div class="flex items-center gap-2 mb-2">
                                  <h4 class="text-sm font-semibold text-gray-700">Document Summary</h4>
                                  <Chip
                                    @intent={{typeInfo.intent}}
                                    @size="sm"
                                  >{{typeInfo.label}}</Chip>
                                </div>
                              {{/let}}
                              <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                {{docSummary.result.general.summary}}
                              </p>
                            </div>
                          {{/if}}

                          {{! Show More/Less Toggle Button }}
                          {{#if
                            (or
                              docSummary.result.receipt
                              docSummary.result.transcript
                              docSummary.is_error
                            )
                          }}
                            <button
                              type="button"
                              class="flex items-center gap-2 text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors mb-3"
                              {{on "click" (fn this.toggleExpanded attachment.id)}}
                            >
                              {{#if (this.isExpanded attachment.id)}}
                                <span>Show Less</span>
                                <PhCaretUp @size="16" />
                              {{else}}
                                <span>Show More</span>
                                <PhCaretDown @size="16" />
                              {{/if}}
                            </button>

                            {{#if (this.isExpanded attachment.id)}}
                              {{! Receipt Information }}
                              {{#if docSummary.result.receipt}}
                                <div
                                  class="mb-4 p-3 bg-green-50 border border-green-100 rounded-lg"
                                >
                                  <h4
                                    class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2"
                                  >
                                    <PhCheckCircle @size="16" class="text-green-600" />
                                    Receipt Details
                                  </h4>
                                  {{#if docSummary.result.receipt.entity}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                      <span class="font-semibold">Entity:</span>
                                      {{docSummary.result.receipt.entity}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.receipt.payer_name}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                      <span class="font-semibold">Payer:</span>
                                      {{docSummary.result.receipt.payer_name}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.receipt.summary}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                      {{docSummary.result.receipt.summary}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.receipt.line_items}}
                                    <div class="mt-2 space-y-1">
                                      {{#each docSummary.result.receipt.line_items as |lineItem|}}
                                        <div
                                          class="text-xs p-2 bg-white dark:bg-gray-800 rounded border border-green-200"
                                        >
                                          <div class="flex items-center justify-between mb-1">
                                            <span
                                              class="font-semibold"
                                            >{{lineItem.line_item}}</span>
                                            {{#if lineItem.line_amount}}
                                              <span class="text-green-700 font-bold">
                                                ${{lineItem.line_amount}}
                                              </span>
                                            {{/if}}
                                          </div>
                                          {{#if lineItem.line_details}}
                                            <p class="text-gray-600">{{lineItem.line_details}}</p>
                                          {{/if}}
                                        </div>
                                      {{/each}}
                                    </div>
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{! Transcript Information }}
                              {{#if docSummary.result.transcript}}
                                <div class="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                  <h4
                                    class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                                  >Transcript Details</h4>
                                  {{#if docSummary.result.transcript.institution}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                      <span class="font-semibold">Institution:</span>
                                      {{docSummary.result.transcript.institution}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.transcript.student_name}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                      <span class="font-semibold">Student:</span>
                                      {{docSummary.result.transcript.student_name}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.transcript.program}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                      <span class="font-semibold">Program:</span>
                                      {{docSummary.result.transcript.program}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.transcript.summary}}
                                    <p class="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                      {{docSummary.result.transcript.summary}}
                                    </p>
                                  {{/if}}
                                  {{#if docSummary.result.transcript.courses}}
                                    {{#if docSummary.result.transcript.courses.length}}
                                      <div class="mt-2 space-y-1">
                                        <p
                                          class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1"
                                        >Courses:</p>
                                        {{#each docSummary.result.transcript.courses as |course|}}
                                          <div
                                            class="text-xs p-2 bg-white dark:bg-gray-800 rounded border border-blue-200"
                                          >
                                            <div class="flex items-center justify-between">
                                              <span class="font-semibold">{{course.course}}</span>
                                              {{#if course.grade}}
                                                <Chip
                                                  @intent="default"
                                                  @size="sm"
                                                >{{course.grade}}</Chip>
                                              {{/if}}
                                            </div>
                                            {{#if course.period}}
                                              <p
                                                class="text-gray-500 dark:text-gray-400 text-xs mt-1"
                                              >{{course.period}}</p>
                                            {{/if}}
                                            {{#if course.course_detail}}
                                              <p
                                                class="text-gray-600 dark:text-gray-400 mt-1"
                                              >{{course.course_detail}}</p>
                                            {{/if}}
                                          </div>
                                        {{/each}}
                                      </div>
                                    {{/if}}
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{! Error State }}
                              {{#if docSummary.is_error}}
                                <div
                                  class="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg"
                                >
                                  <PhWarning
                                    @size="16"
                                    class="text-red-600 dark:text-red-400 flex-shrink-0"
                                  />
                                  <p class="text-sm text-red-700">
                                    Error processing this document
                                  </p>
                                </div>
                              {{/if}}
                            {{/if}}
                          {{/if}}
                        </div>
                      </:body>
                    </Section>
                  {{else}}
                    {{! No document summary available - show basic info }}
                    <Section>
                      <:header>
                        <div class="flex flex-col gap-2">
                          <div class="flex items-center gap-2 w-full">
                            {{#if (@isDeleted attachment)}}
                              <div class="flex flex-col flex-1 min-w-0 gap-1">
                                <div
                                  class="flex items-center gap-2 text-gray-500 dark:text-gray-400 italic min-w-0"
                                >
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    <FileIcon @size="24" class="flex-shrink-0" />
                                  {{/let}}
                                  <span class="block">WHAAA{{attachment.filename}}</span>
                                  <span class="flex-shrink-0 text-sm">(Deleted)</span>
                                </div>
                                {{#if attachment.attachmentDeletedAt}}
                                  <div class="text-xs text-gray-400 dark:text-gray-600 ml-7">
                                    Deleted
                                    {{safeFormatDate
                                      attachment.attachmentDeletedAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                            {{else}}
                              <div class="flex flex-col flex-1 min-w-0 gap-1">
                                <div
                                  class="flex flex-wrap items-center gap-2 min-w-0 overflow-hidden"
                                >
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="flex flex-wrap items-center gap-2 min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                        title="View {{attachment.filename}}"
                                      >
                                        <FileIcon
                                          @size="24"
                                          class="text-gray-500 dark:text-gray-400 flex-shrink-0"
                                        />
                                        <span
                                          class="font-semibold block truncate"
                                        >{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon
                                        @size="24"
                                        class="text-gray-500 dark:text-gray-400 flex-shrink-0"
                                      />
                                      <span
                                        class="font-semibold block truncate max-w-96"
                                      >{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                </div>
                                {{#if attachment.createdAt}}
                                  <div class="text-xs text-gray-500 dark:text-gray-400 ml-7">
                                    Uploaded
                                    {{safeFormatDate
                                      attachment.createdAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <div class="flex items-center gap-2 flex-shrink-0 ml-auto">
                                {{#if @deleteAttachment}}
                                  <button
                                    type="button"
                                    class="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Delete attachment"
                                    aria-label="Delete attachment"
                                    {{on "click" (fn this.confirmDelete attachment)}}
                                  >
                                    <PhTrash @size="18" />
                                  </button>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                      </:header>
                      <:body>
                        <p class="text-sm text-gray-500">
                          Processing document analysis...
                        </p>
                      </:body>
                    </Section>
                  {{/if}}
                {{/let}}
              {{else}}
                {{! No asset summary available }}
                <Section>
                  <:header>
                    <div class="flex flex-col gap-2">
                      <div class="flex items-center gap-2 w-full">
                        {{#if (@isDeleted attachment)}}
                          <div class="flex flex-col flex-1 min-w-0 gap-1">
                            <div
                              class="flex items-center gap-2 text-gray-500 dark:text-gray-400 italic min-w-0"
                            >
                              {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                <FileIcon @size="24" class="flex-shrink-0" />
                              {{/let}}
                              <span class="block">SUP{{attachment.filename}}</span>
                              <span class="flex-shrink-0 text-sm">(Deleted)</span>
                            </div>
                            {{#if attachment.attachmentDeletedAt}}
                              <div class="text-xs text-gray-400 dark:text-gray-600 ml-7">
                                Deleted
                                {{safeFormatDate
                                  attachment.attachmentDeletedAt
                                  month="numeric"
                                  day="numeric"
                                  year="2-digit"
                                  hour="numeric"
                                  minute="2-digit"
                                }}
                              </div>
                            {{/if}}
                          </div>
                        {{else}}
                          <div class="flex flex-col flex-1 min-w-0 gap-1">
                            <div class="flex items-center gap-2 min-w-0 overflow-hidden">
                              {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                {{#if attachment.signedUrl}}
                                  <a
                                    href={{attachment.signedUrl}}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="flex items-center gap-2 min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                    title="View {{attachment.filename}}"
                                  >
                                    <FileIcon
                                      @size="24"
                                      class="text-gray-500 dark:text-gray-400 flex-shrink-0"
                                    />
                                    <span
                                      class="font-semibold block truncate"
                                    >{{attachment.filename}}</span>
                                  </a>
                                {{else}}
                                  <FileIcon
                                    @size="24"
                                    class="text-gray-500 dark:text-gray-400 flex-shrink-0"
                                  />
                                  <span
                                    class="font-semibold block truncate max-w-96"
                                  >{{attachment.filename}}</span>
                                {{/if}}
                              {{/let}}
                            </div>
                            {{#if attachment.createdAt}}
                              <div class="text-xs text-gray-500 dark:text-gray-400 ml-7">
                                Uploaded
                                {{safeFormatDate
                                  attachment.createdAt
                                  month="numeric"
                                  day="numeric"
                                  year="2-digit"
                                  hour="numeric"
                                  minute="2-digit"
                                }}
                              </div>
                            {{/if}}
                          </div>
                          <div class="flex items-center gap-2 flex-shrink-0 ml-auto">
                            {{#if @deleteAttachment}}
                              <button
                                type="button"
                                class="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete attachment"
                                aria-label="Delete attachment"
                                {{on "click" (fn this.confirmDelete attachment)}}
                              >
                                <PhTrash @size="18" />
                              </button>
                            {{/if}}
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </:header>
                  <:body>
                    <p class="text-sm text-gray-500">
                      No AI analysis available for this attachment
                    </p>
                  </:body>
                </Section>
              {{/if}}
            {{/let}}
          {{/each}}
        {{else}}
          <div class="text-center py-8">
            <PhFile @size="48" class="text-gray-300 mx-auto mb-2" />
            <p class="text-sm text-gray-500">No attachments found</p>
          </div>
        {{/if}}
      </div>
    </div>

    {{! Confirmation Modal for Delete }}
    <Modal @isOpen={{this.showConfirmModal}} @onClose={{this.cancelDelete}} @size="sm" as |m|>
      <m.Header>
        <h3 class="text-lg font-semibold">Delete Attachment</h3>
      </m.Header>
      <m.Body>
        <p class="text-sm text-gray-700">
          Are you sure you want to delete
          {{#if this.attachmentToDelete}}
            {{#let (this.getAssetSummaryForAttachment this.attachmentToDelete) as |assetSummary|}}
              {{#if assetSummary}}
                {{#let (this.getDocumentSummaryFromAssetSummary assetSummary) as |docSummary|}}
                  {{#if docSummary}}
                    <strong>"{{docSummary.file_name}}"</strong>
                  {{else}}
                    this attachment
                  {{/if}}
                {{/let}}
              {{else}}
                this attachment
              {{/if}}
            {{/let}}
          {{else}}
            this attachment
          {{/if}}? This action cannot be undone.
        </p>
      </m.Body>
      <m.Footer>
        <ModalFooter
          @onCancel={{this.cancelDelete}}
          @onSubmit={{this.performDelete}}
          @submitText="Delete"
          @submitIntent="danger"
          @cancelText="Cancel"
          @isRunning={{@deleteAttachment.isRunning}}
        />
      </m.Footer>
    </Modal>
  </template>
}

export default AttachmentsAnalysisView;
